// pages/mypage/MyPage.tsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MyPageLayout from "../../components/mypage/MyPageLayout";
import { useAuthStore } from "../../store/authStore";
import { getApiUrl, fetchWithAuth } from "../../api";

interface MemberHomeInfo {
  login_id: string;
  name: string;
  unique_member_no: string;
  total_points: number;
  total_attendance_days: number;
}

interface MemberMe {
  name: string;
  login_id: string;
  phone: string;
  email: string;
  affiliation_name: string | null;
  grade: string | null;
}

const makeIcon = (children: React.ReactNode) => (
  <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="#f5a623" strokeWidth={1.6}>
    {children}
  </svg>
);

const QUICK_LINKS = [
  {
    label: "마루 포인트 내역",
    href: "/mypage/point",
    icon: makeIcon(<><circle cx="12" cy="12" r="9" /><text x="12" y="17" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#f5a623" stroke="none">P</text></>),
    desc: "적립 및 사용 내역 확인",
  },
  {
    label: "출석현황",
    href: "/mypage/attendance",
    icon: makeIcon(<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><path d="M9 16l2 2 4-4" /></>),
    desc: "월별 출석 현황 보기",
  },
  {
    label: "도서 대출/반납",
    href: "/mypage/book",
    icon: makeIcon(<><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>),
    desc: "대출 중인 도서 확인",
  },
  {
    label: "책 신청",
    href: "/mypage/book-request",
    icon: makeIcon(<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></>),
    desc: "희망 도서 신청하기",
  },
];

export default function MyPage() {
  const { name: storeName } = useAuthStore();
  const [me, setMe] = useState<MemberMe | null>(null);
  const [homeInfo, setHomeInfo] = useState<MemberHomeInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const [verified, setVerified] = useState(
    () => sessionStorage.getItem("mypage_verified") === "true"
  );
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwChecking, setPwChecking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [meRes, homeRes] = await Promise.all([
          fetchWithAuth(getApiUrl("/marubit/api/member/me")),
          fetchWithAuth(getApiUrl("/marubit/api/member/me/home-info")),
        ]);
        if (meRes.ok) setMe(await meRes.json());
        if (homeRes.ok) setHomeInfo(await homeRes.json());
      } catch (e) {
        console.error("회원 정보 로딩 실패:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleVerifyPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!pwInput.trim()) { setPwError("비밀번호를 입력해주세요."); return; }
    if (!me?.login_id) { setPwError("사용자 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요."); return; }

    setPwChecking(true);
    setPwError("");
    try {
      const res = await fetch(getApiUrl("/marubit/api/member/login"), {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ grant_type: "password", username: me.login_id, password: pwInput }),
      });
      if (res.ok) {
        setVerified(true);
        sessionStorage.setItem("mypage_verified", "true");
        setPwInput("");
      } else {
        setPwError("비밀번호가 올바르지 않습니다.");
      }
    } catch {
      setPwError("서버 연결 오류가 발생했습니다.");
    } finally {
      setPwChecking(false);
    }
  };

  if (!verified) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-[#fafafa]">
        <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] px-8 py-8 w-[340px]">
          <div className="flex flex-col items-center mb-6">
            <div className="w-[52px] h-[52px] rounded-full bg-[#fdf7f0] border border-[#fde9c0] flex items-center justify-center mb-3">
              <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="#f5a623" strokeWidth={1.8}>
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <p className="m-0 text-[17px] font-bold text-[#3d2f2a]">본인 확인</p>
            <p className="m-0 mt-1 text-[13px] text-gray-400 text-center">마이페이지 접근을 위해<br />비밀번호를 입력해주세요.</p>
          </div>
          <form onSubmit={handleVerifyPassword} className="flex flex-col gap-3">
            <input
              type="password"
              value={pwInput}
              onChange={e => { setPwInput(e.target.value); setPwError(""); }}
              placeholder="비밀번호 입력"
              autoFocus
              className="w-full h-11 px-4 border border-[#e5e7eb] rounded-lg text-[14px] text-[#3d2f2a] outline-none focus:border-[#887267] transition-colors"
            />
            {pwError && <p className="m-0 text-[12px] text-red-500">{pwError}</p>}
            <button
              type="submit"
              disabled={pwChecking}
              className="w-full h-[44px] bg-[#887267] text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pwChecking ? "확인 중..." : "확인"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <MyPageLayout title="마이페이지">
      <div className="flex flex-col gap-5">

        {/* 회원 정보 카드 */}
        <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(136,114,103,0.08)] border border-[#f0ebe7] overflow-hidden">
          <div className="bg-gradient-to-r from-[#887267] to-[#a08070] px-7 py-6 flex items-center gap-5">
            <div className="w-[60px] h-[60px] rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <svg viewBox="0 0 24 24" width={32} height={32} fill="none" stroke="white" strokeWidth={1.5}>
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div>
              <p className="m-0 text-white/70 text-[13px]">안녕하세요 👋</p>
              <p className="m-0 text-white text-[20px] font-bold mt-[2px]">
                {homeInfo?.name ?? storeName ?? "-"}
                <span className="text-[15px] font-medium ml-1">님</span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 divide-x divide-[#f0ebe7]">
            <div className="flex flex-col items-center py-5 gap-1">
              <p className="m-0 text-[12px] text-gray-400 font-medium">아이디</p>
              <p className="m-0 text-[15px] text-[#3d2f2a] font-semibold">
                {loading ? "-" : (homeInfo?.login_id ?? "-")}
              </p>
            </div>
            <div className="flex flex-col items-center py-5 gap-1">
              <p className="m-0 text-[12px] text-gray-400 font-medium">보유 마루</p>
              <p className="m-0 text-[15px] text-[#f5a623] font-bold">
                {loading ? "-" : `${(homeInfo?.total_points ?? 0).toLocaleString()}마루`}
              </p>
            </div>
            <div className="flex flex-col items-center py-5 gap-1">
              <p className="m-0 text-[12px] text-gray-400 font-medium">누적 출석</p>
              <p className="m-0 text-[15px] text-[#887267] font-bold">
                {loading ? "-" : `${homeInfo?.total_attendance_days ?? 0}일`}
              </p>
            </div>
          </div>
        </div>

        {/* 빠른 메뉴 */}
        {/* <div>
          <p className="m-0 mb-3 text-[14px] font-semibold text-[#555]">바로가기</p>
          <div className="grid grid-cols-2 gap-3">
            {QUICK_LINKS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="no-underline bg-white rounded-xl border border-[#f0ebe7] px-5 py-4 flex items-center gap-4 hover:shadow-[0_4px_16px_rgba(245,166,35,0.15)] hover:border-[#f5a623]/40 transition-all duration-150"
              >
                <div className="w-[44px] h-[44px] rounded-[12px] bg-[#fffbf2] border border-[#fde9c0] flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <div>
                  <p className="m-0 text-[14px] font-bold text-[#3d2f2a]">{item.label}</p>
                  <p className="m-0 text-[12px] text-gray-400 mt-[2px]">{item.desc}</p>
                </div>
                <svg className="ml-auto shrink-0" viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#ccc" strokeWidth={2}>
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </Link>
            ))}
          </div>
        </div> */}

        {/* 내 정보 */}
        <div className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_12px_rgba(136,114,103,0.08)] px-7 py-5">
          <p className="m-0 mb-4 text-[15px] font-bold text-[#3d2f2a]">내 정보</p>
          <div className="divide-y divide-[#f5f0ec]">
            {[
              { label: "이름", value: me?.name },
              { label: "아이디", value: me?.login_id },
              { label: "휴대폰번호", value: me?.phone },
              { label: "이메일", value: me?.email },
              { label: "소속명", value: me?.affiliation_name },
              { label: "학년", value: me?.grade ? `${me.grade}학년` : null },
            ].map((row) => (
              <div key={row.label} className="flex items-center py-3">
                <span className="w-[100px] shrink-0 text-[13px] text-gray-400 font-medium">{row.label}</span>
                <span className="text-[14px] text-[#3d2f2a] font-medium">
                  {loading ? <span className="text-gray-300">불러오는 중...</span> : (row.value ?? "-")}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Link
              to="/mypage/edit"
              className="no-underline inline-flex items-center gap-2 px-5 py-[9px] bg-[#887267] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#6b5248] transition-colors duration-150"
            >
              정보 수정
            </Link>
          </div>
        </div>

      </div>
    </MyPageLayout>
  );
}